import type { FileToResolve, StagingBomNode, StagingTriage } from './staging-types'

/**
 * UID-497 (T10) scope (a) item 1 — "every drawing available in this session",
 * the comp's `drawPool` (`Fictiv Platform.dc.html:9280-9281`:
 * `[...new Set([...ownDraws, ...orphanDraws])]`).
 *
 * Three sources, deduped, in a stable order:
 *
 * 1. the identified parts' OWN associated drawings (`ownDraws`),
 * 2. ORPHAN drawings from loose uploaded files — `issue: 'orphan-drawing'`
 *    only, never the whole files-to-resolve list,
 * 3. drawings already attached to other BOM leaves, so a drawing generated or
 *    uploaded on one custom row can be reused on another (the comp has no
 *    equivalent because its custom rows are file-backed; without this a
 *    generated drawing would be invisible to every other row).
 *
 * ## Why `issue === 'orphan-drawing'` and not every file to resolve
 *
 * `filesToResolve` also carries `no-drawing` (a CAD MODEL missing its drawing),
 * `duplicate` and `no-match` rows. A `.step` model is not a drawing, and
 * offering it here would let a human attach one as this line's "drawing" — the
 * server's own pool refuses it, so offering it would only produce a 404 the
 * human cannot explain. This mirrors `sessionDrawingPool` in
 * `apps/api/.../node-configuration-verbs.ts`; the server's copy is
 * AUTHORITATIVE (it re-resolves whatever filename the client sends), and this
 * one exists so the menu offers the same set the server will accept.
 *
 * ## Why the result is `FileToResolve[]` when only the FILENAME is used
 *
 * `DrawingAssociationControl` is shared with the part-scoped rows, whose
 * `associate` really does take a `fileId`. Rather than fork that component's
 * prop surface, the pool is expressed in its existing shape and entries with no
 * backing file row carry their own filename as the id — a LOCAL handle only:
 * `StagingBomLeafRow` maps it back to the filename before calling the node
 * verb, which is what the server re-resolves. Nothing sends these ids anywhere.
 */
export function buildSessionDrawingPool(triage: StagingTriage): readonly FileToResolve[] {
  const pool: FileToResolve[] = []
  const seen = new Set<string>()
  const add = (filename: string | null | undefined, fileId?: string): void => {
    if (filename == null || filename === '' || seen.has(filename)) return
    seen.add(filename)
    pool.push({ fileId: fileId ?? filename, filename, issue: 'orphan-drawing' })
  }

  for (const part of triage.identified) {
    add(part.drawingFilename)
  }
  for (const file of triage.filesToResolve) {
    if (file.issue === 'orphan-drawing') {
      add(file.filename, file.fileId)
    }
  }
  const walk = (nodes: readonly StagingBomNode[]): void => {
    for (const node of nodes) {
      add(node.drawingFilename)
      walk(node.children)
    }
  }
  walk(triage.bomHierarchy ?? [])
  return pool
}

/**
 * UID-497 (T10) — how long the demo pretends to generate a drawing before it
 * attaches, matching the comp's own `setTimeout(…, 1900)` (`dc.html:9322`).
 *
 * ONE named constant, per the ticket's own note: this is a DEMO AFFORDANCE, not
 * a CAD pipeline, so it must be tunable (or deletable) from one place rather
 * than hunted for. The wait is deliberately CLIENT-side — the server's
 * `generate` arm attaches immediately — because it exists purely so the
 * "Generating…" chip is visible; putting it in the request would make a real
 * endpoint slow on purpose.
 */
export const DEMO_DRAWING_GENERATE_DELAY_MS = 1900
